import { useState, type ReactNode } from "react";
import {
  Archive,
  BookOpenCheck,
  CalendarCheck,
  Crosshair,
  Download,
  Flame,
  RotateCcw,
  ScrollText,
  Swords,
  UserRound,
  Volume2,
  VolumeX,
} from "lucide-react";
import { currentDayStreak, progressSummary } from "../../engine";
import { isMuted, toggleMuted } from "../../fx/sfx";
import { useGame } from "../../state/GameProvider";
import { UserPanel } from "../user/UserPanel";
import { BrandSeal } from "./BrandSeal";
import { SyncStatus } from "../user/SyncStatus";
import { SettingsMenu } from "./SettingsMenu";

function Stat({
  label,
  value,
  accent,
  warn,
  icon,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
  icon?: ReactNode;
}) {
  return (
    <div className={`stat${accent ? " accent" : ""}${warn ? " warn" : ""}`}>
      <span className="stat-label">
        {icon}
        {label}
      </span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

export function TopBar() {
  const { state, actions } = useGame();
  const summary = progressSummary(state.progress);
  const cleared = summary.mastered === summary.total;
  const dayStreak = currentDayStreak(state.progress);
  const missCount = state.progress.misses?.length ?? 0;
  const [muted, setMuted] = useState(isMuted());
  const [userOpen, setUserOpen] = useState(false);

  function handleReset() {
    if (window.confirm("確定清空本機通關進度？此操作只影響當前瀏覽器。")) actions.reset();
  }

  return (
    <>
    <header className="topbar">
      <div className="brand">
        <BrandSeal />
        <span className="brand-text">
          <strong>義戰論語</strong>
          <small>{cleared ? "全書通關 · 義理已立" : `掌握全部 ${summary.total} 章 · 含楊伯峻譯註`}</small>
        </span>
      </div>

      <div className="topstats">
        <Stat label="掌握" value={`${summary.mastered}/${summary.total}`} accent />
        <Stat label="門關" value={`${summary.gates}`} />
        <Stat label="連擊" value={`${state.duel.combo}`} icon={<Flame size={11} />} />
        {dayStreak > 0 && <Stat label="連日" value={`${dayStreak}天`} accent icon={<CalendarCheck size={11} />} />}
        <Stat label="專注" value={`${state.progress.focus}`} />
        {summary.due > 0 && <Stat label="待複習" value={`${summary.due}`} warn />}
      </div>

      <div className="topactions">
        {missCount > 0 && (
          <button className="tb-btn warn" onClick={() => actions.next("wrong")} title="重攻最近失手的章句">
            <Crosshair size={15} />
            <span className="tb-label">攻錯</span>
            <b>{missCount}</b>
          </button>
        )}
        {summary.due > 0 && (
          <button className="tb-btn warn" onClick={() => actions.next("review")} title="進入到期複習">
            <ScrollText size={15} />
            <span className="tb-label">複習</span>
            <b>{summary.due}</b>
          </button>
        )}
        <a
          className="tb-btn"
          href="https://img.bdfz.net/apps/lunyu-battle/latest.apk"
          aria-label="下載義戰論語 Android 版"
          title="下載 Android 版"
        >
          <Download size={15} />
          <span>Android</span>
        </a>
        <button
          className={`tb-btn${state.learnMode ? " on" : ""}`}
          onClick={actions.toggleLearn}
          title="切換純學習 / 對戰模式"
        >
          {state.learnMode ? <Swords size={15} /> : <BookOpenCheck size={15} />}
          <span>{state.learnMode ? "對戰" : "純學習"}</span>
        </button>
        <div className="user-menu">
          <button
            className={`tb-btn${userOpen ? " on" : ""}`}
            onClick={() => setUserOpen((open) => !open)}
            title="Seiue 用戶與排行榜"
            aria-expanded={userOpen}
          >
            <UserRound size={15} />
            用戶
          </button>
          {userOpen && (
            <div className="user-popover">
              <UserPanel />
            </div>
          )}
        </div>
        <SettingsMenu />
        <button
          className={`tb-btn${muted ? "" : " on"}`}
          onClick={() => setMuted(toggleMuted())}
          title={muted ? "開啟音效" : "靜音"}
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <button className="tb-btn" onClick={actions.exportSave} title="匯出存檔">
          <Archive size={15} />
        </button>
        <button className="tb-btn" onClick={handleReset} title="重置本機進度">
          <RotateCcw size={15} />
        </button>
      </div>
    </header>
    <SyncStatus />
    </>
  );
}
